import React, { CSSProperties, Suspense, useCallback, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { Dimmer, Label, Loader, Menu, Segment, Sidebar } from 'semantic-ui-react'
import { useWindowSize } from '../../../hooks/useWindowSize'
import { IMenuItem } from '../../../types/menuItems'
import { IUserDetail } from '../../../types/user'
import { checkAdminStatus, checkRole } from '../../../utils/checkRole'
import { projectSubmenu } from '../../../utils/projectSubmenu'
import { IProject, InsightsViewType, ViewType } from '../../project/types/project'
import { adminSubMenuItems } from '../constants/menuItems'
import VersionLabel from '../../../components/VersionLabel/VersionLabel'
const ConfirmCloseModal = React.lazy(() => import('../../../components/Modal/ConfirmDeleteModal'))

const PUSHER_BORDER_STYLE = {
  borderTopLeftRadius: '15px',
  borderTopRightRadius: '0px',
  borderBottomLeftRadius: '0px',
  borderBottomRightRadius: '0px'
}

interface Props {
  visible: boolean
  activeMenuItem: IMenuItem
  activeSubMenuItem: IMenuItem
  actDropdownMenuItem: IMenuItem
  mainContent: JSX.Element
  headerContent: JSX.Element | null
  selectedProject: IProject
  viewType: ViewType
  formOpenAndDirty: boolean
  openModal: boolean
  user: IUserDetail | null
  setFormOpen: (open: boolean) => void
  setFormDirty: (dirty: boolean) => void
  setOpenConfirmModal: (open: boolean) => void
  setSubMenuItem: (item: string, path: string) => void
  setDropdownMenuItem: (item: string, path: string) => void
  setInsightsViewType: (viewType: InsightsViewType) => void
  filterEmployees: (search: string) => void
  setViewType: (viewType: ViewType) => void
}

const SidebarPushable = (props: Props): JSX.Element => {
  const {
    visible,
    mainContent,
    headerContent,
    viewType,
    activeSubMenuItem,
    activeMenuItem,
    formOpenAndDirty,
    openModal,
    user,
    selectedProject,
    setOpenConfirmModal,
    setFormOpen,
    setFormDirty,
    filterEmployees,
    setViewType
  } = props

  const [redirectPath, setRedirectPath] = useState({ item: '', path: '' })
  const [renderModal, setRenderModal] = useState(false)
  const [headerHeight, setHeaderHeight] = useState(0)
  const headerRef = React.useRef<HTMLDivElement | null>(null)

  const _setHeaderHeight = useCallback(() => {
    setTimeout(() => {
      setHeaderHeight(headerRef.current?.parentElement?.clientHeight ?? 0)
    }, 500)
  }, [headerRef])

  const _history = useHistory()
  const _location = useLocation()
  const _width = useWindowSize().width
  const _height = useWindowSize().height
  const isSettingsPage = _location.pathname.includes('/budget/settings')

  useEffect(() => {
    _setHeaderHeight()
  })

  const handleMenuItemClicked = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, i: IMenuItem, path: string) => {
    if (!formOpenAndDirty) {
      if (activeSubMenuItem.name === 'user') {
        filterEmployees('')
      }
      if (viewType === 'timeline' && activeSubMenuItem.name !== 'activities') {
        setViewType('list')
      }
      if (activeSubMenuItem.name !== i.name.toLowerCase()) {
        _history.push(path)
        props.setSubMenuItem(i.name.toLowerCase(), i.path)
      }
    } else {
      setOpenConfirmModal(true)
      setRenderModal(true)
      setRedirectPath({ item: i.name.toLowerCase(), path: i.path })
    }
  }

  const confirmClose = (action: 'cancel' | 'delete') => {
    if (action === 'delete') {
      setFormOpen(false)
      setFormDirty(false)
      props.setSubMenuItem(redirectPath.item, redirectPath.path)
      if (props.selectedProject.id !== '') {
        _history.push(`${activeMenuItem.path}/${props.selectedProject.id}${redirectPath.path}`)
      } else {
        _history.push(`${activeMenuItem.path}${redirectPath.path}`)
      }
    }
    setOpenConfirmModal(false)
    setRenderModal(false)
  }

  const renderSubMenu = () => {
    const _projectSubMenuItems: IMenuItem[] = projectSubmenu(user, selectedProject)
    switch (activeMenuItem.name) {
      case 'projects':
        return (
          _projectSubMenuItems.map(i => {
            if (user && (checkRole(user, i.rights) || checkAdminStatus(user, selectedProject))) {
              return (
                <Menu.Item
                  key={i.name}
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    height: '45px',
                    textAlign: 'start',
                    background: activeSubMenuItem.name === i.name.toLowerCase() ? '#E6FAF5' : '',
                    color: activeSubMenuItem.name === i.name.toLowerCase() ? '#08D6A1' : '',
                    padding: '16px 12px',
                    marginLeft: '9px',
                    marginRight: '9px',
                    marginBottom: '8px',
                    fontSize: '18px',
                    letterSpacing: '0.25px'
                  }}
                  active={activeSubMenuItem.name === i.name.toLowerCase()}
                  content={i.name}
                  onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => handleMenuItemClicked(e, i, `${i.parentPath}/${props.selectedProject.id}${i.path}`)}
                />
              )
            }
            return null
          })
        )
      case 'admin':
        return (
          adminSubMenuItems.map(i => {
            if (user && checkRole(user, i.rights)) {
              return (
                <Menu.Item
                  key={i.name}
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    height: '45px',
                    textAlign: 'start',
                    background: activeSubMenuItem.name === i.name.toLowerCase() ? '#E6FAF5' : '',
                    color: activeSubMenuItem.name === i.name.toLowerCase() ? '#08D6A1' : '',
                    padding: '16px 20px',
                    marginLeft: '20px',
                    marginRight: '20px',
                    marginBottom: '8px',
                    fontSize: '18px',
                    letterSpacing: '0.25px'
                  }}
                  active={activeSubMenuItem.name === i.name.toLowerCase()}
                  content={i.name}
                  onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => i.disabled ? null : handleMenuItemClicked(e, i, `${i.parentPath}${i.path}`)}
                />
              )
            } else {
              return null
            }
          })
        )
    }
  }

  const sidebarTitle = () => {
    if (activeMenuItem.name === 'projects') {
      return 'Project Space'
    }

    if (activeMenuItem.name === 'admin') {
      return 'Admin'
    }
  }

  const menuTitle: CSSProperties = {
    color: sidebarTitle() === 'Admin' ? '#232C58' : '#172533',
    margin: sidebarTitle() === 'Admin' ? '27px 0px 20px 40px' : '32px 0px 27px 22px',
    textAlign: 'start',
    padding: sidebarTitle() === 'Admin' ? '0' : '0',
    fontSize: sidebarTitle() === 'Admin' ? '2.2rem' : '20.25px',
    fontWeight: 600,
    letterSpacing: sidebarTitle() === 'Admin' ? '' : '0.25px',
  }

  const _value = activeMenuItem.name === 'notification' ? 0 : activeMenuItem.name === 'projects' ? 220 : 280

  const calcScrollOn = () => {
    if ((activeMenuItem.name === 'projects' && visible) || activeMenuItem.name === 'admin') {
      return `${1360 - _value}px`
    }
    return '1360px'
  }

  const calcHeight = () => {
    if (_width < 1440) {
      return 28
    }
    return 15
  }

  const handleSetFocusedId = () => {
    // Focus tracking removed - this function is now a no-op
  }

  return (
    <React.Fragment>
      <Sidebar.Pushable as={Segment} style={{ ...PUSHER_BORDER_STYLE, height: '100%', width: '100%', overflow: 'hidden', border: 'none', marginTop: 0 }}>
        <Sidebar
          style={{ width: `${_value}px`, borderRight: sidebarTitle() === 'Admin' ? 'none' : '1px solid #BEC8C3', boxShadow: 'none' }}
          as={Menu}
          animation='push'
          icon='labeled'
          vertical
          visible={visible}
          width='thin'
          borderless
        >
          <Menu.Item style={menuTitle}>{sidebarTitle()}</Menu.Item>
          {renderSubMenu()}
        </Sidebar>
        <Sidebar.Pusher style={{
          background: '#F4F7F9',
          transition: 'all 0.5s ease',
          left: visible ? `${(_value - 150)}px` : 0,
          width: visible ? `calc(100% - ${_value}px)` : '100%',
          overflowX: 'auto'
        }}>
          {activeMenuItem.path !== '/profile' ?
            <Segment.Group
              style={{
                margin: '15px 0 0',
                background: 'transparent',
                padding: '0 0 20px',
                border: 0,
                minWidth: calcScrollOn(),
                height: `${_height - calcHeight()}px`
              }}>
              <Segment
                id='trialytixHeader'
                className={isSettingsPage ? 'settingsPage' : ''}
                basic
                style={{
                  padding: activeMenuItem.name === 'projects' && activeSubMenuItem.name !== '' ? '17px 52px 0 40px' :
                    activeMenuItem.name === 'admin' ? '0 52px 0 40px' :
                      '15px 52px 0 40px'
                }}>
                <div ref={headerRef}>
                  {headerContent}
                </div>
              </Segment>
              <div
                id='main-content'
                style={{
                  margin: '20px 0 0px',
                  background: 'transparent',
                  padding: '0 0 20px',
                  border: 0,
                  overflowY: viewType === 'timeline' && activeSubMenuItem.name === 'activities' ? 'hidden' : 'auto',
                  overflowX: 'auto',
                  height: headerContent ? `calc(100vh - ${(headerHeight + 60)}px)` : `calc(100vh - 20px)`
                }}
                onScroll={() => handleSetFocusedId()}
                >
                <Segment basic style={{ background: 'transparent', padding: '0 40px', border: 0 }}>
                  {mainContent}
                </Segment>
              </div>
            </Segment.Group>
            : <Segment style={{ height: '100vh', overflowY: 'scroll', overflowX: 'hidden', padding: '0 0 40px', border: 'none', borderRadius: 0 }}>{mainContent}</Segment>}
        </Sidebar.Pusher>
        <Suspense fallback={<Dimmer active inverted><Loader size='big' /></Dimmer>}>
          <ConfirmCloseModal open={openModal && renderModal} closeModal={true} userInput={confirmClose} />
        </Suspense>
      </Sidebar.Pushable>
      <VersionLabel style={{ float: 'right', position: 'fixed', margin: '0.5rem', bottom: '10px', right: '10px' }} />
    </React.Fragment>
  )
}

export default SidebarPushable
